/**
 * Device orientation (FEAT-023) — Momento never turns sideways.
 *
 * The manifest already declares this app portrait-only (`vite.config.ts`), and
 * Android and desktop PWAs honour it. iOS is the only platform that doesn't,
 * and Safari supports neither `screen.orientation.lock()` nor the manifest's
 * orientation member — so we can't ask. We rotate back instead: the app renders
 * inside a portrait-shaped box that gets turned into the landscape viewport.
 * An H×W box rotated 90° occupies exactly W×H, so it fills the screen with no
 * letterboxing and nothing on screen appears to move when the phone turns.
 *
 * The rules live here and nowhere else, so the frame, the recording canvas and
 * the photo canvas can never disagree about which way is up.
 */

export interface FrameOrientation {
  /** Are we counter-rotating right now? */
  pinned: boolean;
  /** Degrees to rotate the app by: 0, 90 or -90. */
  rotation: number;
}

/**
 * Above this, it's a window and not a phone — a desktop browser in a landscape
 * window must never be turned on its side.
 */
const PHONE_SHORT_SIDE = 600;

/** Which way the SCREEN has turned. 0 when nothing can tell us. */
export function readAngle(): number {
  const modern = window.screen?.orientation?.angle;
  if (typeof modern === 'number') return ((modern % 360) + 360) % 360;
  // iOS < 16.4 has no screen.orientation, only this deprecated one.
  const legacy = (window as unknown as { orientation?: number }).orientation;
  if (typeof legacy === 'number') return ((legacy % 360) + 360) % 360;
  return 0;
}

/**
 * How far to turn the app back. 0 and 180 are both portrait — an upside-down
 * phone is still a tall screen, so there is nothing to correct.
 */
export function counterRotation(angle: number, shortSide: number): FrameOrientation {
  const a = ((Math.round(angle) % 360) + 360) % 360;
  if (a !== 90 && a !== 270) return { pinned: false, rotation: 0 };
  if (!(shortSide > 0) || shortSide > PHONE_SHORT_SIDE) return { pinned: false, rotation: 0 };
  return { pinned: true, rotation: a === 90 ? -90 : 90 };
}

/** Read the live device state. */
export function measureOrientation(): FrameOrientation {
  return counterRotation(readAngle(), Math.min(window.innerWidth, window.innerHeight));
}

/**
 * The cached answer. The recording canvas reads this 30 times a second, so it
 * must never touch `window.innerWidth` — that would force a layout on every
 * frame. Refreshed only when the device actually tells us something changed.
 */
let current: FrameOrientation = { pinned: false, rotation: 0 };

/** Degrees the app is currently turned by. Safe to call in a hot loop. */
export function frameRotation(): number {
  return current.rotation;
}

/**
 * The app's own viewport. Turned, the frame is as wide as the window is tall —
 * so anything animating "off the bottom of the screen" must travel the FRAME's
 * height, not `window.innerHeight`, or it stops halfway and vanishes.
 */
export function frameViewport(): { width: number; height: number } {
  const w = window.innerWidth;
  const h = window.innerHeight;
  return current.pinned ? { width: h, height: w } : { width: w, height: h };
}

/**
 * A drag, in the app's own axes.
 *
 * `clientX`/`clientY` are viewport coordinates and CSS transforms don't touch
 * them — so inside a turned frame, a swipe the person sees as "up" arrives as
 * a sideways drag. Every gesture that reads a DIRECTION has to come through
 * here. (Distances and pinch spans don't: a magnitude survives a rotation.)
 *
 * Identity when the app is upright, which is every case but one.
 */
export function frameDelta(dx: number, dy: number): { dx: number; dy: number } {
  const r = current.rotation;
  // `0 - n` rather than `-n`: negating zero gives -0, which compares unequal to
  // 0 under Object.is and is a trap waiting for whoever writes the next test.
  if (r === -90) return { dx: 0 - dy, dy: dx };
  if (r === 90) return { dx: dy, dy: 0 - dx };
  return { dx, dy };
}

/**
 * A viewport point in the coordinate space of an element, given that element's
 * `getBoundingClientRect()`.
 *
 * The rect of a turned element is its on-screen box — at exactly ±90° that IS
 * the element, just with its width and height swapped, so this stays exact.
 * Returns pixels from the element's own top-left, in its own axes.
 */
export function framePointIn(
  rect: { top: number; right: number; bottom: number; left: number },
  x: number,
  y: number,
): { x: number; y: number } {
  const r = current.rotation;
  if (r === -90) return { x: rect.bottom - y, y: x - rect.left };
  if (r === 90) return { x: y - rect.top, y: rect.right - x };
  return { x: x - rect.left, y: y - rect.top };
}

/** That element's size in its OWN axes — rect.width/height are swapped when turned. */
export function frameRectSize(rect: { width: number; height: number }): {
  width: number;
  height: number;
} {
  return current.pinned
    ? { width: rect.height, height: rect.width }
    : { width: rect.width, height: rect.height };
}

/**
 * ONE set of listeners for the whole app, started by the first subscriber and
 * stopped by the last.
 *
 * Deliberately not one DOM watcher per subscriber: `apply()` only notifies on
 * a CHANGE, and whichever watcher ran first would already have updated
 * `current`, so every other watcher would decide nothing had happened and stay
 * silent forever.
 */
const subscribers = new Set<(state: FrameOrientation) => void>();
let unwatch: (() => void) | null = null;

/**
 * Watch the device. iOS fires these three inconsistently — sometimes only one
 * of them, sometimes before the viewport has resized — so we listen to all
 * three and re-measure on the next frame as well as immediately.
 */
function startWatching(): () => void {
  let frame = 0;

  const apply = () => {
    const next = measureOrientation();
    if (next.pinned === current.pinned && next.rotation === current.rotation) return;
    current = next;
    subscribers.forEach((fn) => fn(next));
  };

  const recheck = () => {
    apply();
    // iOS can fire the event before window.innerWidth/innerHeight have caught
    // up with the new orientation, which reads as "still portrait".
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(apply);
  };

  const mql = window.matchMedia?.('(orientation: landscape)');
  mql?.addEventListener('change', recheck);
  window.screen?.orientation?.addEventListener?.('change', recheck);
  window.addEventListener('orientationchange', recheck);
  window.addEventListener('resize', recheck);

  current = measureOrientation();

  return () => {
    cancelAnimationFrame(frame);
    mql?.removeEventListener('change', recheck);
    window.screen?.orientation?.removeEventListener?.('change', recheck);
    window.removeEventListener('orientationchange', recheck);
    window.removeEventListener('resize', recheck);
  };
}

/** Subscribe to orientation changes. Fires immediately with the current state. */
export function watchOrientation(onChange: (state: FrameOrientation) => void): () => void {
  subscribers.add(onChange);
  if (!unwatch) unwatch = startWatching();
  onChange(current);

  return () => {
    subscribers.delete(onChange);
    if (subscribers.size === 0) {
      unwatch?.();
      unwatch = null;
    }
  };
}

/**
 * The same thing shaped for `useSyncExternalStore`, so a component can render
 * from the orientation instead of only reading it in a callback.
 *
 * Needed because the frame passes `children` straight through: when it
 * re-renders on a rotation, React sees the same element object and skips the
 * subtree entirely. Anything below that has to LOOK different when the phone
 * turns must subscribe for itself. Pair it with `frameRotation` as the
 * snapshot — a number, so the snapshot is stable by construction.
 */
export function subscribeOrientation(onStoreChange: () => void): () => void {
  return watchOrientation(onStoreChange);
}
